import type {
  HivePluginConfig,
  PlayerConfig,
  PlayerSources,
  PluginsConfig,
  SettingsConfig,
  WatermarkConfig,
} from '@movingimage-evp/player-library';
import type { VodChapter } from 'src/generated/graphql-viewer';

import type { ECdnSettings, HiveToken } from '../../../generated/graphql-viewer';
import type { UserInfoJwt } from '../../../utils/parse-jwt';

import * as FingerprintJS from '@fingerprintjs/fingerprintjs';
import { SourceType } from '@movingimage-evp/player-library';
import { hash } from 'hash-it';
import { useEffect, useState } from 'react';

import { ENV } from '../../../config/env';
import { debugEnabled, devLog, expiresAt } from '../../../utils/dev-log';
import { parseJwt } from '../../../utils/parse-jwt';
import { getSessionStorageItem } from '../../../utils/session-storage';
import { getUserInformation } from '../../../utils/user-information-utils';
import { getQueryTestParameters } from '../../hooks/use-read-query-test-parameters';

export type LsproPlayerConfig = {
  webcastId: string;
  contentId?: string;
  playbackUrl?: string;
  title?: string;
  accountId?: string;
  lsproId?: string;
  primaryColor?: string;
  iconColor?: string;
  logoUrl?: string;
  hiveConfiguration?: HiveToken | null;
  vBrickConfiguration?: ECdnSettings['vBrick'];
  kollectiveConfiguration?: ECdnSettings['kollective'];
  state?: string;
  channelName?: string;
  channelId?: string;
  videoManagerId?: string;
  groupId?: string;
  language?: string;
  primaryLanguage?: string;
  muted?: boolean;
  autoplay?: boolean;
  hidePlaybackSpeed?: boolean;
  watermarkContent?: string;
  chapters?: VodChapter[];
  audioOnly?: boolean;
};

export function usePlayerConfig(config?: LsproPlayerConfig): (PlayerConfig & { visitorId: string }) | undefined {
  const [playerConfig, setPlayerConfig] = useState<(PlayerConfig & { visitorId: string }) | undefined>();

  useEffect(() => {
    if (!config) return;

    const { playbackUrl } = config;

    if (!playbackUrl) return;

    // Ignore late async results from an older render so they cannot overwrite
    // a newer player config after language/source changes or unmount.
    let stale = false;

    const updatePlayerConfig = async () => {
      let visitorId = '';
      const queryUserId = getAllowedQueryVisitorId(config.lsproId);

      try {
        // For specific events, we allow passing a visitorId through the query parameters for testing purposes.
        // In case the value is not null or undefined, we use it as the visitorId, otherwise we generate a random one.
        visitorId = queryUserId ?? (await generateVisitorId(config.webcastId));
      } finally {
        if (!stale) {
          setPlayerConfig({ ...getPlayerConfig({ ...config, visitorId, playbackUrl }), visitorId: visitorId });
        }
      }
    };

    updatePlayerConfig();

    return () => {
      stale = true;
    };
  }, [config]);

  return playerConfig;
}

// TODO: Temporary - LSPRO-3290
const customHostLsproIds = [
  'rsmfTMvaZUG1ytj5ntR6r', // Union Investment Gruppe - Test
  'u5UWFvnw23QhKWcLLvV73', // Union Investment
  'U6CsTtwRBPX3YwiOOgEAv', // Deka
  'VkCDhc50o0UlhhBZhqQv2', // movingimage Onboarding
  '0iO8WeqPxO9YzV1jN76BG', // LSPRO Squad - Prod 2
  'WebCats', // Our QA environment
];

// TODO: Temporary - LSPRO-3508
const allowedVisitorIdLsproIds = [
  'fWV3rEP5DVaJPkl8MgFf0', // QA Kollective enabled
  'kDAZDZtK4Pf91vYe4a5mk', // BMW ECDN Test Channel
  'COxPMiHXjRLUFrBKIHgyz', // BMW Group - Staging
  'WebCats', // Our QA environment
];

function getAllowedQueryVisitorId(lsproId?: string): string | undefined {
  if (!allowedVisitorIdLsproIds.includes(lsproId || '')) return undefined;

  return getQueryTestParameters().userId;
}

function getPlugins({
  webcastId,
  contentId,
  title,
  accountId,
  lsproId,
  hiveConfiguration,
  vBrickConfiguration,
  kollectiveConfiguration,
  state,
  channelName,
  channelId,
  videoManagerId,
  groupId,
  language,
  visitorId,
}: LsproPlayerConfig & { visitorId: string }): PluginsConfig {
  const youboraAnalytics = {
    accountCode: ENV.YOUBORA_ACCOUNT_CODE,
    ...(customHostLsproIds.includes(lsproId || '') ? { host: `${window.location.origin}/${ENV.YOUBORA_HOST}` } : {}),
    ...(visitorId && { 'user.name': visitorId }),
    'content.title': title || '',
    'content.id': contentId || '',
    'content.channel': channelName || '',
    'content.language': language || '',
    'content.customDimension.1': videoManagerId || '',
    'content.customDimension.3': channelId || '',
    'content.customDimension.6': groupId || '',
    'content.customDimension.7': accountId || '',
    'content.customDimension.8': 'LiveStream Pro',
    'content.customDimension.9': state,
    'content.customDimension.10': webcastId,
    'content.customDimension.11': lsproId,
    'content.customDimension.12': 'useNetworkInformationApi:false',
  };

  const hiveConfig: HivePluginConfig | undefined = hiveConfiguration
    ? {
        eventName: title,
        token: {
          videoId: webcastId,
          jwt: hiveConfiguration.jwt || '',
          manifest: hiveConfiguration.manifest || '',
        },
        ...(debugEnabled() && { debugLevel: 'debug' }),
      }
    : undefined;

  const vBrick = vBrickConfiguration
    ? {
        ramp: {
          id: webcastId,
          title: title,
          vbrickApiKey: vBrickConfiguration.vbrickApiKey || '',
          vbrickApiSecret: vBrickConfiguration.vbrickApiSecret || '',
          vbrickHostName: vBrickConfiguration.vbrickHostName || '',
          vbrickJwtKeyName: vBrickConfiguration.vbrickJwtKeyName || '',
          omnicacheUris: vBrickConfiguration.vBrickOmnicacheUris
            ? vBrickConfiguration.vBrickOmnicacheUris.join(',')
            : '',
        },
      }
    : {};

  // In case the userToken is not set, Kollective takes care of generating a random one.
  const kollective = kollectiveConfiguration
    ? {
        kollective: {
          jitc: kollectiveConfiguration.jitc || '',
          integratorToken: kollectiveConfiguration.jwt || '',
          ...(visitorId && { userToken: visitorId }),
        },
      }
    : {};

  if (kollectiveConfiguration) {
    const integratorToken = parseJwt<{ exp: number; sub: string; metadata: { playType: string } }>(
      kollectiveConfiguration.jwt || ''
    );

    if (integratorToken) {
      const eventType =
        typeof integratorToken.sub === 'string'
          ? ((): string => {
              const parts = integratorToken.sub.split('-');
              return parts.length > 1 ? parts[1] : integratorToken.sub;
            })()
          : '(unknown)';

      const expiryInfo =
        typeof integratorToken.exp === 'number' ? expiresAt(integratorToken.exp * 1000 - Date.now()) : '(unknown)';

      devLog(
        'Kollective',
        kollective.kollective,
        '\n Event type -',
        eventType,
        '\n integratorToken expires',
        expiryInfo
      );
    }
  }

  return {
    youboraAnalytics,
    ...(hiveConfig ? { hive: hiveConfig } : {}),
    ...vBrick,
    ...kollective,
  };
}

function getSources(src: string): PlayerSources {
  return {
    hls: {
      src,
      type: SourceType.HLS,
    },
  };
}

function getSettings(
  primaryColor?: string,
  iconColor?: string,
  logoUrl?: string,
  muted?: boolean,
  autoplay?: boolean,
  hidePlaybackSpeed?: boolean,
  audioOnly?: boolean
): SettingsConfig {
  return {
    liveUI: true,
    hideLoopButton: true,
    primaryColor,
    iconColor,
    logoUrl,
    maxPlaylistRetries: 0,
    autoplay,
    videoLayout: 'fill',
    muted,
    hidePlaybackSpeed,
    hideErrorDialog: true,
    audioOnly,
  };
}

function getWatermark(content?: string): WatermarkConfig {
  return {
    content: content || '',
  };
}

function getChapters(chapters?: VodChapter[], language?: string, primaryLanguage?: string): PlayerConfig['chapters'] {
  if (!chapters || !chapters.length || !language) return undefined;

  return {
    [language]: chapters.map(({ offsetInMs, titles, thumbnailUrl }) => {
      const translatedTitle = titles.find((title) => title.language === language)?.text;
      const fallbackTitle = titles.find((title) => title.language === primaryLanguage)?.text;

      return {
        start: offsetInMs,
        title: translatedTitle || fallbackTitle || '',
        thumbnail: thumbnailUrl || '',
      };
    }),
  };
}

// Generates a hashed visitor ID based on SSO ID, registration email or fingerprint
async function generateVisitorId(eventId: string): Promise<string> {
  const { email } = getUserInformation(eventId) || {};
  const jwt = parseJwt<UserInfoJwt>(getSessionStorageItem('viewerAccessToken'));
  const ssoId = jwt ? jwt.employeeId + ':' + jwt.email : undefined;

  let id = '';
  if (ssoId) id = ssoId;
  else if (email) id = email;
  else id = (await (await FingerprintJS.load()).get()).visitorId;

  return hash(eventId + ':' + id).toString();
}

function getPlayerConfig(
  config: LsproPlayerConfig & {
    visitorId: string;
    playbackUrl: string;
  }
): PlayerConfig {
  const queryUserId = getAllowedQueryVisitorId(config.lsproId);
  const disableBotDetection = queryUserId !== undefined;

  return {
    sources: getSources(config.playbackUrl),
    settings: getSettings(
      config.primaryColor,
      config.iconColor,
      config.logoUrl,
      config.muted,
      config.autoplay,
      config.hidePlaybackSpeed,
      config.audioOnly
    ),
    metadata: { title: config.title },
    watermark: getWatermark(config.watermarkContent),
    plugins: getPlugins(config),
    chapters: getChapters(config.chapters, config.language, config.primaryLanguage),
    lng: config.language,
    analyticsMode: 'full',
    tweaks: { disableNetworkInformationApi: true },
    ...(disableBotDetection ? { disableBotDetection: true } : {}),
  };
}
